<script setup>
import dayjs from 'dayjs';
import { flatten, groupBy, orderBy, uniqBy } from 'lodash-es';
import { computed } from 'vue';
import HawkTreeSelect from '~/common/components/organisms/hawk-tree/hawk-treeselect.vue';
import { useTerraStore } from '~/terra/store/terra.store';
import { useTerraHelperComposable } from '~/terra/utils/helper-composable.js';

const props = defineProps({
  progressData: {
    type: Object,
    default: () => ({}),
  },
});

const emits = defineEmits(['close', 'filter', 'export']);

const $t = inject('$t');
const { getProjectOptions } = useTerraHelperComposable();
const terra_store = useTerraStore();

const date_options = [
  [
    'exact-date',
    'Exact date',
    null,
    null,
  ],
  [
    'today',
    'Today',
    dayjs().startOf('day').format('YYYY-MM-DD'),
    dayjs().endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'yesterday',
    'Yesterday',
    dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD'),
    dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'last-7-days',
    'Last 7 days',
    dayjs().subtract(7, 'day').startOf('day').format('YYYY-MM-DD'),
    dayjs().endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'last-30-days',
    'Last 30 days',
    dayjs().subtract(30, 'day').startOf('day').format('YYYY-MM-DD'),
    dayjs().endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'week-to-date',
    'Week to date',
    dayjs().startOf('week').format('YYYY-MM-DD'),
    dayjs().endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'this-week',
    'This week',
    dayjs().startOf('week').format('YYYY-MM-DD'),
    dayjs().endOf('week').format('YYYY-MM-DD'),
  ],
  [
    'last-week',
    'Last week',
    dayjs().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
    dayjs().subtract(1, 'week').endOf('week').format('YYYY-MM-DD'),
  ],
  [
    'month-to-date',
    'Month to date',
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'this-month',
    'This month',
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('month').format('YYYY-MM-DD'),
  ],
  [
    'last-month',
    'Last month',
    dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
  ],
  [
    'quarter-to-date',
    'Quarter to date',
    dayjs().startOf('quarter').format('YYYY-MM-DD'),
    dayjs().endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'this-quarter',
    'This quarter',
    dayjs().startOf('quarter').format('YYYY-MM-DD'),
    dayjs().endOf('quarter').format('YYYY-MM-DD'),
  ],
  [
    'last-quarter',
    'Last quarter',
    dayjs().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD'),
    dayjs().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD'),
  ],
  [
    'year-to-date',
    'Year to date',
    dayjs().startOf('year').format('YYYY-MM-DD'),
    dayjs().endOf('day').format('YYYY-MM-DD'),
  ],
  [
    'this-year',
    'This year',
    dayjs().startOf('year').format('YYYY-MM-DD'),
    dayjs().endOf('year').format('YYYY-MM-DD'),
  ],
  [
    'last-year',
    'Last year',
    dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
    dayjs().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
  ],
  [
    'custom',
    'Custom',
    null,
    null,
  ],
];

const form = ref({
  options: {
    is_xlsx: true,
    is_csv: false,
  },
  export_type: 'all',
  projects: [],
  workflows: [],
  date_range: null,
  start_date: null,
  end_date: null,
  exact_date: null,
});

const projects_list = computed(() => getProjectOptions());

const workflow_list = computed(() => {
  const workflows = Object.values(terra_store.terra_workflows);
  const workflow_uids_available = Object.keys(groupBy(props.progressData, 'workflow_uid'));
  const filtered_workflows = workflows.filter(wf => workflow_uids_available.includes(wf.uid));

  const data = orderBy(filtered_workflows, wf => wf.name.toLowerCase(), ['asc']).map((wf) => {
    const child = uniqBy(
      flatten(
        Object.entries(wf.data || {})
          .sort((a, b) => a[1].order_index - b[1].order_index)
          .map(([_, item]) => item.fields),
      ),
      f => f.uid,
    );
    return {
      name: wf.name,
      uid: wf.uid,
      fields: child,
    };
  });
  return data;
});

const date_range_options = computed(() => {
  return date_options.map(([value, label, start_date, end_date]) => ({
    value,
    label: $t(label),
    start_date,
    end_date,
  }));
});

function onSave() {
  const filters = {
    export_type: form.value.export_type,
    projects: form.value.projects,
    fields: form.value.activities,
    date_range: form.value?.date_range?.value || null,
  };
  if (form.value.date_range?.value === 'custom') {
    filters.start_date = form.value.start_date;
    filters.end_date = form.value.end_date;
  }
  else if (form.value.date_range?.value === 'exact-date') {
    filters.start_date = form.value.exact_date;
    filters.end_date = form.value.exact_date;
  }
  else {
    filters.start_date = form.value.date_range?.start_date;
    filters.end_date = form.value.date_range?.end_date;
  }
  if (form.value.options.is_csv) {
    filters.selected_export_option = 'export_bi';
  }
  if (form.value.options.is_xlsx) {
    if (form.value.options.xlsx_type === 'detailed') {
      filters.selected_export_option = 'export';
    }
    else {
      filters.selected_export_option = 'export_summary';
    }
  }
  emits('filter', filters);
}
</script>

<template>
  <hawk-modal-container id="terra-charts-export-popup" content_class="!w-[700px]">
    <Vueform
      v-model="form"
      size="sm"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      sync
      :display-errors="false"
      :endpoint="onSave"
    >
      <div class="col-span-12">
        <hawk-modal-header @close="emits('close')">
          <template #title>
            {{ $t('Export data') }}
          </template>
        </hawk-modal-header>
        <hawk-modal-content>
          <ObjectElement :label="$t('Export as')" name="options" class="mb-5">
            <div class="col-span-12">
              <div class="flex flex-col gap-y-2">
                <RadioElement
                  name="is_xlsx"
                  text="XLSX"
                  radio-value="true"
                  :add-classes="{
                    RadioElement: {
                      wrapper: 'gap-y-1',
                    },
                  }"
                  @input="form.options.is_csv = null; form.options.xlsx_type = 'summary'"
                />
                <RadiogroupElement
                  v-show="form.options.is_xlsx"
                  class="ml-4"
                  name="xlsx_type"
                  :items="[
                    { label: $t('Summary'), value: 'summary' },
                    { label: $t('Summary + Activity sheets'), value: 'detailed' },
                  ]"
                  default="summary"
                />
                <RadioElement
                  name="is_csv"
                  text="CSVs"
                  radio-value="true"
                  :add-classes="{
                    RadioElement: {
                      wrapper: 'gap-y-1',
                    },
                  }"
                  @input="form.options.is_xlsx = null"
                />
              </div>
            </div>
          </ObjectElement>
          <RadiogroupElement
            name="export_type"
            class="mb-5"
            :label="$t('Summary')"
            :items="[
              {
                label: $t('All'),
                description: $t('Select All to export data for all layers and activities across the entire project timeline'),
                value: 'all',
              },
              {
                label: $t('Custom'),
                description: $t('Select Custom to apply filters and export data for specific layers, activities, and a defined date range'),
                value: 'custom',
              },
            ]"
          >
            <template #radio="{ item, value, el$, classes, isDisabled, id, name }">
              <div>
                <div class="flex items-start mt-2">
                  <input
                    :id="id"
                    v-model="el$.value"
                    type="radio"
                    :value="value"
                    :class="classes.input"
                    :name="name"
                    :disabled="isDisabled"
                    :aria-label="item.label"
                  >

                  <span :class="classes.text">
                    {{ item.label }}
                  </span>
                </div>
                <p class="text-xs text-gray-500">
                  {{ item.description }}
                </p>
              </div>
            </template>
          </RadiogroupElement>
          <template v-if="form.export_type === 'custom'">
            <HawkTreeSelect
              class="mb-5"
              :options="{
                name: 'projects',
                label: $t('Layers'),
                placeholder: $t('Select layer'),
                appendTo: '#terra-charts-export-popup',
              }"
              select_type="LEAF_PRIORITY"
              children_key="projects"
              label_key="name"
              value_key="uid"
              :data="projects_list"
              @update-form="form.projects = $event"
            />
            <HawkTreeSelect
              class="mb-5"
              :options="{
                name: 'activities',
                label: $t('Activities'),
                placeholder: `${$t('Select')} ${$t('activities')}`,
                appendTo: '#terra-charts-export-popup',
              }"
              children_key="fields"
              label_key="name"
              value_key="uid"
              select_type="LEAF_PRIORITY"
              :data="workflow_list"
              @update-form="form.activities = $event"
            />
            <SelectElement
              name="date_range"
              class="mb-5"
              :label="$t('Time Range')"
              :search="true"
              :native="false"
              :object="true"
              :placeholder="$t('Select time range')"
              :can-deselect="false"
              :can-clear="false"
              :items="date_range_options"
              append-to="#terra-charts-export-popup"
            >
              <template #after>
                <div v-if="form.date_range?.value === 'custom'" class="flex items-center justify-between mt-4 gap-x-4">
                  <DateTimeElement
                    name="start_date"
                    class="w-[50%]"
                    :rules="['required']"
                    :options="{
                      format: 'MMM d, yyyy',
                      teleport: true,
                      placeholder: $t('Select start date'),
                    }"
                  />
                  <DateTimeElement
                    name="end_date"
                    class="w-[50%]"
                    :rules="['required']"
                    :options="{
                      minDate: form?.start_date ? new Date(form.start_date) : null,
                      format: 'MMM d, yyyy',
                      teleport: true,
                      placeholder: $t('Select end date'),
                    }"
                  />
                </div>
                <div v-if="form.date_range?.value === 'exact-date'" class="mt-4">
                  <DateTimeElement
                    name="exact_date"
                    class="w-[50%]"
                    :rules="['required']"
                    :options="{
                      format: 'MMM d, yyyy',
                      teleport: true,
                      placeholder: $t('Select date'),
                    }"
                  />
                </div>
              </template>
            </SelectElement>
          </template>
        </hawk-modal-content>
        <hawk-modal-footer>
          <template #right>
            <div class="flex justify-end items-center">
              <hawk-button
                class="mr-5"
                type="outlined"
                @click="emits('close')"
              >
                {{ $t('Cancel') }}
              </hawk-button>
              <ButtonElement button-class="w-full bg-blue-600" name="submit" :submits="true">
                {{ $t('Export') }}
              </ButtonElement>
            </div>
          </template>
        </hawk-modal-footer>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>
